import React, { useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents,
  Polyline,
} from "react-leaflet";

// Fix default icon paths (Webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const makeDivIcon = (bg, label) =>
  L.divIcon({
    className: "",
    html: `<div style="background:${bg};color:#fff;width:34px;height:34px;display:flex;align-items:center;justify-content:center;font-weight:700;font-family:'DM Sans',sans-serif;border:2px solid #111;box-shadow:2px 2px 0 #111;">${label}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });

const pickupIcon = makeDivIcon("#FFB800", "A");
const dropoffIcon = makeDivIcon("#111111", "B");

function buildOccupancyBadgeHTML(occ, animation = "animation:ridegy-occ-badge 4s infinite;") {
  const cfg = {
    empty: { bg: "#00A859", label: "0",    sub: "EMPTY" },
    "1":   { bg: "#1d4ed8", label: "1",    sub: "PAX" },
    "2":   { bg: "#1d4ed8", label: "2",    sub: "PAX" },
    "3":   { bg: "#FF8800", label: "3",    sub: "PAX" },
    "4":   { bg: "#FF8800", label: "4",    sub: "PAX" },
    full:  { bg: "#E11D48", label: "FULL", sub: "" },
  }[occ];
  if (!cfg) return "";
  const isWord = cfg.label.length > 2;
  return `<div style="position:absolute;inset:0;border-radius:9999px;overflow:hidden;
    border:3px solid ${cfg.bg};background:${cfg.bg};color:#fff;
    display:flex;flex-direction:column;align-items:center;justify-content:center;line-height:1;
    font-family:'DM Sans',sans-serif;font-weight:800;text-align:center;
    ${animation}">
    <div style="font-size:${isWord ? 10 : 16}px;">${cfg.label}</div>
    ${cfg.sub ? `<div style="font-size:6px;font-weight:700;opacity:0.85;margin-top:1px;">${cfg.sub}</div>` : ""}
  </div>`;
}

// Speed badge layer for the rider map's other-driver markers. Matches the
// look of the occupancy layer so the same avatar circle just swaps face.
function buildDriverSpeedLayerHTML(kmh, animation = "") {
  const rounded = Math.round(kmh);
  const isBig = rounded >= 100;
  return `<div style="position:absolute;inset:0;border-radius:9999px;overflow:hidden;
    border:3px solid #0f172a;background:#0f172a;color:#fff;
    display:flex;flex-direction:column;align-items:center;justify-content:center;line-height:1;
    font-family:'DM Sans',sans-serif;font-weight:800;text-align:center;
    ${animation}">
    <div style="font-size:${isBig ? 12 : 16}px;">${rounded}</div>
    <div style="font-size:6px;font-weight:700;opacity:0.85;margin-top:1px;letter-spacing:0.05em;">KM/H</div>
  </div>`;
}

function buildDriverAvatarIcon({ picture, initial, occupancy, vehicle_color, speed_kmh, speed_hidden }) {
  const safeInitial = (initial || "D").toString().slice(0, 1).toUpperCase();
  const photoInner = picture
    ? `<img src="${picture}" alt="" referrerpolicy="no-referrer"
         style="width:100%;height:100%;object-fit:cover;display:block;"
         onerror="this.style.display='none';this.nextElementSibling.style.display='flex';" />
       <div style="position:absolute;inset:0;display:none;align-items:center;justify-content:center;
         font-family:'DM Sans',sans-serif;font-weight:700;color:#fff;background:#111;font-size:14px;">
         ${safeInitial}
       </div>`
    : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;
         font-family:'DM Sans',sans-serif;font-weight:700;color:#fff;background:#111;font-size:14px;">
         ${safeInitial}
       </div>`;

  // Rotator config — photo → (occupancy?) → (speed?).
  const showSpeed = !speed_hidden && typeof speed_kmh === "number" && speed_kmh > 0;
  const showOcc = !!occupancy;
  const totalFrames = 1 + (showOcc ? 1 : 0) + (showSpeed ? 1 : 0);
  const cycleDur = totalFrames === 3 ? "6s" : totalFrames === 2 ? "4s" : "0s";
  const slotAnim = (idx) => {
    if (totalFrames < 2) return "";
    return `animation:ridegy-slot-${totalFrames}-${idx} ${cycleDur} infinite;`;
  };

  const colorHex = vehicleColorHex(vehicle_color);
  const colorDot = colorHex
    ? `<div style="position:absolute;bottom:-2px;right:-2px;width:11px;height:11px;background:${colorHex};
         border:2px solid #fff;border-radius:9999px;box-shadow:1px 1px 0 #111;"
         title="${vehicle_color || ""}"></div>`
    : "";

  const photoLayer = `
    <div style="position:absolute;inset:0;border-radius:9999px;overflow:hidden;
      border:3px solid #00A859;background:#fff;box-shadow:2px 2px 0 #111;
      ${slotAnim(0)}">
      ${photoInner}
    </div>`;
  const occLayer = showOcc ? buildOccupancyBadgeHTML(occupancy, slotAnim(1)) : "";
  const speedLayer = showSpeed
    ? buildDriverSpeedLayerHTML(speed_kmh, slotAnim(showOcc ? 2 : 1))
    : "";

  return L.divIcon({
    className: "",
    html: `
      <div style="position:relative;width:38px;height:38px;">
        ${photoLayer}
        ${occLayer}
        ${speedLayer}
        <div style="position:absolute;top:-2px;right:-2px;width:11px;height:11px;background:#00A859;
          border:2px solid #fff;border-radius:9999px;"></div>
        ${colorDot}
      </div>`,
    iconSize: [38, 38],
    iconAnchor: [19, 19],
  });
}

// Map common car-color names → hex swatches. Anything unrecognised returns
// null so we skip the swatch dot rather than showing a random colour.
function vehicleColorHex(name) {
  if (!name) return null;
  const n = String(name).trim().toLowerCase();
  const m = {
    white: "#f8fafc", black: "#111827", silver: "#c0c4cc", grey: "#6b7280", gray: "#6b7280",
    red: "#dc2626", maroon: "#7f1d1d", pink: "#ec4899",
    orange: "#f97316", yellow: "#eab308", gold: "#d4af37", cream: "#fef3c7", beige: "#e5d7b0",
    green: "#16a34a", lime: "#84cc16", teal: "#14b8a6",
    blue: "#2563eb", navy: "#1e3a8a", "sky blue": "#38bdf8", "light blue": "#60a5fa", "dark blue": "#1e3a8a",
    purple: "#7c3aed", violet: "#8b5cf6",
    brown: "#8b5a2b", bronze: "#a97142", tan: "#c8a26a",
  };
  if (m[n]) return m[n];
  const parts = n.split(/\s+/);
  const last = parts[parts.length - 1];
  if (m[last]) return m[last];
  return null;
}

function buildSelfAvatarIcon({ picture, initial, speed_kmh, heading, occupancy }) {
  const safeInitial = (initial || "U").toString().slice(0, 1).toUpperCase();
  const showSpeed = speed_kmh != null && !Number.isNaN(speed_kmh);
  const speedNum = Math.max(0, Math.round(Number(speed_kmh) || 0));
  const moving = showSpeed && speedNum > 0;
  const bubbleBg = moving ? "#FFB800" : "#111";
  const bubbleFg = moving ? "#111" : "#fff";
  const inner = picture
    ? `<img src="${picture}" alt="" referrerpolicy="no-referrer"
         style="width:100%;height:100%;object-fit:cover;display:block;"
         onerror="this.style.display='none';this.nextElementSibling.style.display='flex';" />
       <div style="position:absolute;inset:0;display:none;align-items:center;justify-content:center;
         font-family:'DM Sans',sans-serif;font-weight:700;color:#fff;background:#1d4ed8;font-size:16px;">
         ${safeInitial}
       </div>`
    : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;
         font-family:'DM Sans',sans-serif;font-weight:700;color:#fff;background:#1d4ed8;font-size:16px;">
         ${safeInitial}
       </div>`;
  const arrow = (typeof heading === "number" && !Number.isNaN(heading))
    ? `<div style="position:absolute;top:-8px;left:50%;transform:translateX(-50%) rotate(${heading}deg);
         width:0;height:0;border-left:7px solid transparent;border-right:7px solid transparent;
         border-bottom:10px solid #1d4ed8;filter:drop-shadow(1px 1px 0 #111);"></div>`
    : "";
  const bubble = showSpeed
    ? `<div style="position:absolute;left:50%;top:-26px;transform:translateX(-50%);
          background:${bubbleBg};color:${bubbleFg};border:2px solid #111;
          padding:1px 6px;font-family:'IBM Plex Mono',ui-monospace,monospace;font-size:10px;font-weight:700;
          line-height:1.2;letter-spacing:0.04em;white-space:nowrap;box-shadow:1px 1px 0 #111;">
         ${moving ? `${speedNum} km/h` : "0"}
         <div style="position:absolute;left:50%;bottom:-5px;transform:translateX(-50%);
            width:0;height:0;border-left:4px solid transparent;border-right:4px solid transparent;
            border-top:5px solid #111;"></div>
       </div>`
    : "";
  return L.divIcon({
    className: "",
    html: `
      <div style="position:relative;width:42px;height:42px;">
        ${arrow}
        ${bubble}
        <div style="position:relative;width:42px;height:42px;border-radius:9999px;overflow:hidden;
          border:3px solid #1d4ed8;background:#fff;box-shadow:2px 2px 0 #111;
          ${occupancy ? "animation:ridegy-occ-avatar 4s infinite;" : ""}">
          ${inner}
        </div>
        ${occupancy ? buildOccupancyBadgeHTML(occupancy) : ""}
        <div class="self-pulse-dot" style="position:absolute;top:-2px;right:-2px;width:12px;height:12px;
          background:#22c55e;border:2px solid #fff;border-radius:9999px;"></div>
      </div>`,
    iconSize: [42, 42],
    iconAnchor: [21, 21],
  });
}

function buildCommunityPinIcon(type) {
  const colors = { news: "#E11D48", police: "#1d4ed8", place: "#00A859", hazard: "#FF8800", event: "#FFB800" };
  const glyphs = { news: "📰", police: "🚓", place: "📍", hazard: "⚠️", event: "🎉" };
  const color = colors[type] || "#111";
  const glyph = glyphs[type] || "📍";
  return L.divIcon({
    className: "",
    html: `<div style="width:28px;height:28px;background:${color};border:2px solid #111;border-radius:9999px;display:flex;align-items:center;justify-content:center;font-size:14px;box-shadow:1px 1px 0 #111;">${glyph}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function ClickHandler({ onClick }) {
  useMapEvents({
    click(e) {
      if (onClick) onClick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo([center.lat, center.lng], map.getZoom(), { duration: 0.6 });
  }, [center, map]);
  return null;
}

function FitBounds({ pickup, dropoff, routeCoords }) {
  const map = useMap();
  useEffect(() => {
    if (!pickup || !dropoff) return;
    const pts = (routeCoords && routeCoords.length > 1) ? routeCoords : [[pickup.lat, pickup.lng], [dropoff.lat, dropoff.lng]];
    try {
      const bounds = L.latLngBounds(pts);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15, animate: true, duration: 0.6 });
    } catch { /* leaflet bounds may fail on degenerate input */ }
  }, [pickup?.lat, pickup?.lng, dropoff?.lat, dropoff?.lng, routeCoords, map]);
  return null;
}

function FollowSelf({ self, enabled }) {
  const map = useMap();
  useEffect(() => {
    if (!enabled || !self?.location) return;
    map.panTo([self.location.lat, self.location.lng], { animate: true, duration: 0.5 });
  }, [enabled, self?.location?.lat, self?.location?.lng, map]);
  return null;
}

export default function MapView({
  center,
  zoom = 13,
  pickup,
  dropoff,
  drivers = [],
  self = null,
  followSelf = false,
  communityPins = [],
  onMapClick,
  showRoute = false,
  routeCoords = null,
  recenter,
  className = "h-full w-full",
  dataTestId = "map-view",
}) {
  const fallbackRoutePoints = useMemo(() => {
    if (showRoute && pickup && dropoff && !(routeCoords && routeCoords.length > 1)) {
      return [[pickup.lat, pickup.lng], [dropoff.lat, dropoff.lng]];
    }
    return null;
  }, [showRoute, pickup, dropoff, routeCoords]);

  // Icon cache — see PublicLiveMap for the same technique. Keeps the CSS
  // animation on driver markers alive across polling refreshes by giving
  // Leaflet a stable icon reference per (picture, initial, occupancy,
  // vehicle color, speed bucket). Speed is bucketed to 5 km/h so cruising
  // doesn't restart the rotator.
  const driverIconCacheRef = useRef(new Map());
  const driverIconFor = (d) => {
    const speedShown = !d.speed_hidden && typeof d.speed_kmh === "number" && d.speed_kmh > 0;
    const speedBucket = speedShown ? Math.round(d.speed_kmh / 5) * 5 : 0;
    const sig = `${d.picture || ""}|${d.initial || ""}|${d.occupancy || ""}|${d.vehicle_color || ""}|${speedBucket}|${d.speed_hidden ? "h" : ""}`;
    const cache = driverIconCacheRef.current;
    let icon = cache.get(sig);
    if (!icon) {
      icon = buildDriverAvatarIcon({
        picture: d.picture,
        initial: d.initial,
        occupancy: d.occupancy,
        vehicle_color: d.vehicle_color,
        speed_kmh: speedShown ? speedBucket : 0,
        speed_hidden: d.speed_hidden,
      });
      cache.set(sig, icon);
    }
    return icon;
  };
  const selfIconCacheRef = useRef(new Map());
  const selfIconFor = (s) => {
    const sig = `${s?.picture || ""}|${s?.initial || ""}|${s?.speed_kmh || 0}|${s?.heading || ""}|${s?.occupancy || ""}`;
    const cache = selfIconCacheRef.current;
    let icon = cache.get(sig);
    if (!icon) {
      icon = buildSelfAvatarIcon({ picture: s?.picture, initial: s?.initial, speed_kmh: s?.speed_kmh, heading: s?.heading, occupancy: s?.occupancy });
      cache.set(sig, icon);
    }
    return icon;
  };

  return (
    <div className={className} data-testid={dataTestId}>
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={zoom}
        scrollWheelZoom
        style={{ width: "100%", height: "100%" }}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Recenter center={recenter} />
        <FollowSelf self={self} enabled={followSelf} />
        <FitBounds pickup={pickup} dropoff={dropoff} routeCoords={routeCoords} />
        <ClickHandler onClick={onMapClick} />
        {pickup && (
          <Marker position={[pickup.lat, pickup.lng]} icon={pickupIcon}>
            <Popup>Pickup{pickup.address ? `: ${pickup.address}` : ""}</Popup>
          </Marker>
        )}
        {dropoff && (
          <Marker position={[dropoff.lat, dropoff.lng]} icon={dropoffIcon}>
            <Popup>Drop-off{dropoff.address ? `: ${dropoff.address}` : ""}</Popup>
          </Marker>
        )}
        {self?.location && (
          <>
            {Array.isArray(self.trail) && self.trail.length >= 2 && (
              // Render the breadcrumb trail as a stack of small segments with
              // increasing opacity → the oldest position fades out, the newest
              // is solid. Cheaper than gradient strokes and works on every
              // Leaflet build.
              self.trail.slice(1).map((pt, idx, arr) => {
                const prev = self.trail[idx];
                const opacity = 0.15 + 0.7 * ((idx + 1) / arr.length);
                return (
                  <Polyline
                    key={`self-trail-${idx}`}
                    positions={[prev, pt]}
                    pathOptions={{
                      color: "#1d4ed8",
                      weight: 4,
                      opacity,
                      lineCap: "round",
                    }}
                  />
                );
              })
            )}
            <Marker
            position={[self.location.lat, self.location.lng]}
            icon={selfIconFor(self)}
            zIndexOffset={1000}
          >
            <Popup>
              <div className="leading-tight">
                <div className="font-semibold">You</div>
                {self.speed_kmh != null && (
                  <div className="text-xs opacity-70 mt-0.5">
                    {self.speed_kmh > 0 ? `${Math.round(self.speed_kmh)} km/h` : "Stopped"}
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
          </>
        )}
        {communityPins.map((p) =>
          p.location?.lat != null ? (
            <Marker
              key={`comm-${p.post_id}`}
              position={[p.location.lat, p.location.lng]}
              icon={buildCommunityPinIcon(p.type)}
              zIndexOffset={500}
            >
              <Popup>
                <div className="leading-tight max-w-[220px]">
                  <div className="font-semibold capitalize mb-0.5">{p.type}</div>
                  {p.text && <div className="text-xs mb-1">{p.text}</div>}
                  {p.location?.address && (
                    <div className="text-[10px] opacity-60">{p.location.address}</div>
                  )}
                  <div className="text-[10px] opacity-50 mt-1">{p.user_name}</div>
                </div>
              </Popup>
            </Marker>
          ) : null
        )}
        {drivers.map((d) =>
          d.location ? (
            <React.Fragment key={d.user_id}>
              {Array.isArray(d.trail) && d.trail.length >= 2 && (
                d.trail.slice(1).map((pt, idx, arr) => {
                  const prev = d.trail[idx];
                  const opacity = 0.15 + 0.7 * ((idx + 1) / arr.length);
                  return (
                    <Polyline
                      key={`drv-trail-${d.user_id}-${idx}`}
                      positions={[prev, pt]}
                      pathOptions={{
                        color: "#00A859",
                        weight: 4,
                        opacity,
                        lineCap: "round",
                      }}
                    />
                  );
                })
              )}
              <Marker
                position={[d.location.lat, d.location.lng]}
                icon={driverIconFor(d)}
              >
              <Popup>
                <div className="leading-tight">
                  <div className="font-semibold">
                    {d.vehicle_make} {d.vehicle_model}
                  </div>
                  <div className="text-xs opacity-70 mt-0.5 flex items-center gap-1 flex-wrap">
                    {d.speed_hidden ? (
                      <span>Speed hidden</span>
                    ) : d.speed_kmh != null ? (
                      <span>
                        {d.speed_kmh > 0 ? `${Math.round(d.speed_kmh)} km/h` : "Stopped"}
                      </span>
                    ) : null}
                    {d.distance_km != null && (
                      <>
                        {(d.speed_kmh != null || d.speed_hidden) && <span>·</span>}
                        <span>{d.distance_km} km away</span>
                      </>
                    )}
                  </div>
                  {d.address && (
                    <div className="text-[10px] opacity-60 mt-0.5">{d.address}</div>
                  )}
                </div>
              </Popup>
            </Marker>
            </React.Fragment>
          ) : null
        )}
        {routeCoords && routeCoords.length > 1 && (
          <Polyline
            positions={routeCoords}
            pathOptions={{ color: "#111", weight: 5, opacity: 0.85 }}
          />
        )}
        {fallbackRoutePoints && (
          <Polyline positions={fallbackRoutePoints} pathOptions={{ color: "#111", weight: 3, dashArray: "8 6" }} />
        )}
      </MapContainer>
    </div>
  );
}
